import { useMemo } from 'react'

import { useListEnvironments } from 'api/hooks/useListEnvironments'
import { ListEnvironmentSummary } from 'api/hooks/useListEnvironments/types'
import { useListProjects } from 'api/hooks/useListProjects'
import { ProjectSummary } from 'api/hooks/useListProjects/types'

import { useWorkingCopy } from 'modules/EtlDesigner/hooks/useWorkingCopy'

import { useProjectInfo } from './useProjectInfo'

export interface ProjectInfoNames {
  projectName: string
  environmentName: string
  jobName: string
  warehouse: string
  isError: boolean
}

export const safeNameFromList = (
  list: ListEnvironmentSummary[],
  id: string
): string => {
  const item = list.find((prj) => prj.id === id)
  return item?.name || ''
}

export const safeWarehouseFromList = (
  list: ProjectSummary[],
  id: string
): string => {
  const item = list.find((prj) => prj.id === id)
  return item?.warehouse.toLowerCase() || ''
}

export const useProjectNames: () => ProjectInfoNames = () => {
  const { environmentId, projectId } = useProjectInfo()
  const { data: projectList, isError: isProjectError } = useListProjects()
  const { data: environmentList, isError: isEnvError } = useListEnvironments()
  const { summary, isError: isJobError } = useWorkingCopy()

  const { projectName, environmentName, jobName, warehouse, isError } =
    useMemo<ProjectInfoNames>(() => {
      if (!projectList || !environmentList || !summary) {
        return {
          projectName: '',
          environmentName: '',
          jobName: '',
          warehouse: '',
          isError: true
        }
      }

      const projectDefault = safeNameFromList(projectList.results, projectId)
      const warehouseDefault = safeWarehouseFromList(
        projectList.results,
        projectId
      )
      const envDefault = safeNameFromList(
        environmentList.results,
        environmentId
      )

      return {
        projectName: projectDefault,
        environmentName: envDefault,
        jobName: summary.name,
        warehouse: warehouseDefault.toLowerCase(),
        isError: isEnvError || isProjectError || isJobError
      }
    }, [
      projectList,
      environmentList,
      projectId,
      environmentId,
      summary,
      isEnvError,
      isProjectError,
      isJobError
    ])

  return {
    projectName,
    environmentName,
    jobName,
    warehouse,
    isError
  }
}
